// src/utils/impersonation.js
//
// Shared owner-admin "view as tenant" helpers.
//
// Many pages don't take a loggedInUser prop — they fetch the signed-in user's
// own /users/{uid} document directly and read tenantId from it. For those pages,
// overriding a prop is not enough. Instead, right after they build their user
// object from Firestore, they pass it through applyOwnerImpersonation(), which —
// ONLY for admin@loadmemo.com and ONLY when a tenant has been selected in the
// "View Tenant" screen — swaps in that tenant's id and grants full in-tenant
// visibility. For every other user it returns the object untouched.
//
// The selected tenant lives in sessionStorage under the same key used by
// AdminViewContext, so both mechanisms stay in sync.

export const OWNER_ADMIN_EMAIL = 'admin@loadmemo.com';
export const IMPERSONATION_STORAGE_KEY = 'lm_impersonatedTenant';

// Returns { id, companyName } | null
export const getImpersonatedTenant = () => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * If the owner-admin is currently viewing a tenant, return a copy of userObj
 * scoped to that tenant (with full Super Admin visibility). Otherwise return
 * userObj unchanged.
 *
 * @param {Object} userObj  The user object a page just built from Firestore.
 * @param {string} [email]  Explicit auth email, used when userObj may not carry
 *                          an `email` field (some pages omit it).
 */
export const applyOwnerImpersonation = (userObj, email) => {
  if (!userObj) return userObj;
  const effectiveEmail = email || userObj.email;
  if (effectiveEmail !== OWNER_ADMIN_EMAIL) return userObj;

  const imp = getImpersonatedTenant();

  // The platform owner is ALWAYS Super Admin, whether or not a tenant is
  // selected. This used to be granted only while impersonating, so outside the
  // "View Tenant" screen the owner fell back to whatever their own user
  // document said and got blocked by ordinary role checks. The Firestore rules
  // already grant this account blanket access, so the UI was the odd one out.
  const elevated = {
    ...userObj,
    email: effectiveEmail,
    role: 'Super Admin',
    roles: ['Super Admin'],
  };

  // Only the TENANT swap depends on impersonation — without a selected tenant
  // the owner stays in their own tenant, just with full rights.
  if (!imp || !imp.id) return elevated;

  return withViewAsUser({
    ...elevated,
    tenantId: imp.id,
    assignedParentCompanyIds: undefined,
    assignedCompanyId: undefined,
    _impersonating: true,
    _ownerEmail: effectiveEmail,
    _impersonatedCompanyName: imp.companyName,
  }, imp);
};

// ----------------------------------------------------------------------------
// VIEW AS A PERSON
// ----------------------------------------------------------------------------
// Inside a tenant, the owner can also pick one person — a dispatcher, a Main
// Admin — and see the app exactly as they do: their role, companies, teams and
// driver links decide every list, because every page scopes by these fields.
//
// This is a view, not a login. The browser is still signed in as the owner, so
// Firestore rules are the owner's. But anything SAVED in this mode carries the
// person's id (createdBy, dispatcherId, audit userId), because the app takes
// those from this object — the banner says so in plain words.

// Fields copied from the person's user document. Explicit, so nothing large
// or private (tokens, device ids) ends up in sessionStorage.
export const VIEW_AS_FIELDS = [
  'name', 'role', 'active', 'teamIds', 'formerTeams',
  'assignedParentCompanyIds', 'assignedParentCompanyNames', 'assignedCompanyId',
  'manualDriverAccess', 'permissions',
];

/** Firestore Timestamps -> ISO strings, so the snapshot survives JSON. */
const plain = (v) => {
  if (v && typeof v.toDate === 'function') return v.toDate().toISOString();
  if (Array.isArray(v)) return v.map(plain);
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, plain(x)]));
  }
  return v;
};

/** The snapshot stored as tenant.asUser when the owner picks a person. */
export function makeViewAsUser(userId, userDoc) {
  const profile = {};
  for (const f of VIEW_AS_FIELDS) {
    if (userDoc?.[f] !== undefined) profile[f] = plain(userDoc[f]);
  }
  return { uid: userId, email: userDoc?.email || null, profile };
}

/** Swap in the chosen person, if one is selected. Otherwise unchanged. */
export function withViewAsUser(ownerTenantView, imp) {
  const asUser = imp?.asUser;
  if (!asUser?.uid) return ownerTenantView;
  return {
    ...ownerTenantView,
    assignedParentCompanyIds: undefined,
    assignedCompanyId: undefined,
    ...asUser.profile,
    roles: undefined,
    uid: asUser.uid,
    id: asUser.uid,
    email: asUser.email,
    tenantId: imp.id,
    _viewingAsUser: true,
    _viewingAsName: asUser.profile?.name || asUser.email,
  };
}
